/* eslint-disable camelcase, max-len, import/no-unresolved */
import React from 'react';

import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { useSelector } from 'react-redux';

import ProductComponent from '../../components/Card/Product';
import CategorySliderBar from '../../components/CategorySliderBar';
import ModalComponent from '../../components/Modal';
import StoreCourseGrid from '../../components/Shop/CourseGrid';
import StoreCourseSlider from '../../components/Shop/CourseSlider';
import ShopHeaderComponent from '../../components/Shop/Header';
import ShopSectionComponent from '../../components/Shop/Section';
import StoreFAQComponent from '../../components/Shop/StoreFAQ';
// Components
import SupportComponent from '../../components/Shop/Support';
import StoreTestimonialSlider from '../../components/Shop/TestimonialSlider';

// Hooks
import { useMoengageEventTracker } from '../../hooks/useMoengage';
import useSourceScreen from '../../hooks/useSourceScreen';

import wrapper from '../../redux';

import DiscountIcon from '../../assets/images/store/discount.svg';
import ThunderIcon from '../../assets/images/store/thunder.svg';
import TimeGlassIcon from '../../assets/images/store/time-glass.svg';

// Utils
import { getAuthState, javaApi } from '../../utils/AxiosInstance';

// Container
import ShopContainer from '../../container/Shop';
import styles from '../../styles/Store.module.scss';

function SectionIcon({ theme }) {
  if (theme === 'ORANGE') return <TimeGlassIcon />;
  if (theme === 'GREEN') return <DiscountIcon />;
  if (theme === 'PURPLE') return <ThunderIcon />;

  return null;
}

function SectionContentWrapper({ section, courses, showPrice }) {
  const [selectedCategory, setSelectedCategory] = React.useState(null);
  const filteredCourses = React.useMemo(() => {
    let finalCourses = [];

    if (section?.type !== 'CHIPS') {
      finalCourses = courses || [];
    } else if (selectedCategory === null) {
      finalCourses = courses[0]?.sectionList || [];
    } else {
      const currentCategory = courses.find((course) => course.slug === selectedCategory);

      finalCourses = currentCategory?.sectionList || [];
    }

    return finalCourses.filter((course) => course?.basePrice !== 0);
  }, [section?.type, courses, selectedCategory]);

  React.useEffect(() => {
    if (section) setSelectedCategory(null);
  }, [section]);

  if (filteredCourses.length === 0) return null;
  return (
    <>
      {section.type === 'CHIPS' && (
        <CategorySliderBar.Colored categories={courses} onChange={setSelectedCategory} position="bottom" />
      )}

      {section.displayType ? (
        <StoreCourseSlider products={filteredCourses} theme={section.theme} section={section} showPrice={showPrice} />
      ) : (
        <StoreCourseGrid className={styles.bestSellingCourses}>
          {filteredCourses?.map((course) => (
            <ProductComponent
              key={`${section.sectionId}-${course.courseId}`}
              product={course}
              section={section}
              showPrice={showPrice}
              responsive
            />
          ))}
        </StoreCourseGrid>
      )}
    </>
  );
}

function ViewAllPage({ category, content, showPrice = false }) {
  const screen = useSourceScreen();
  const trackMoengageEvent = useMoengageEventTracker();

  const [currentSection, setCurrentSection] = React.useState(null);
  const [categories, setCategories] = React.useState([]);

  const { storeUserStatus } = useSelector((store) => store.auth.authUser || {});

  const finalShowPrice = React.useMemo(() => !!(storeUserStatus ?? showPrice), [showPrice, storeUserStatus]);

  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      const screen_array = sessionStorage?.getItem('sourceScreen_Array')?.split(',');
      try {
        trackMoengageEvent('store_visited', {
          screen_name: 'state-exams',
          screen_source: screen || (screen_array || [])[screen_array.length - 1],
          position: 'myAccount',
        });
      } catch (error) {
        // Handle error
      }
      javaApi
        .get('store-ws/api/v1/store/list')
        .then((res) => setCategories(res.data?.data?.menuList || []))
        .catch(() => null);
    }
  }, []);

  const showViewMoreModel = (section) => () => {
    setCurrentSection(section);
  };

  return (
    <ShopContainer metaData={content.meta}>
      {(content?.topBanner || content?.meta?.menuHeader) && (
        <ShopHeaderComponent topBanner={content?.topBanner} courseMeta={content?.meta} />
      )}

      <CategorySliderBar categories={categories} position="top" useLink />

      {/* Render Sections */}
      {content.sections?.map((section) => {
        let ThemedSectionComponent = ShopSectionComponent;
        if (section?.theme) {
          ThemedSectionComponent = ShopSectionComponent.Colored;
        }
        let courses = section.sectionList;
        if (section.chipData) {
          courses = section.chipData;
        }

        if (courses?.filter((course) => course?.basePrice !== 0).length === 0) return null;

        return (
          <ThemedSectionComponent
            icon={section.theme && <SectionIcon theme={section?.theme?.toUpperCase()} />}
            key={section.sectionId}
            title={section.heading}
            subtitle={section.subHeading}
            theme={section.theme}
            className={styles.bestSellingSection}
            actionItem={
              section?.sectionList?.length > 4 && (
                <button type="button" className={styles.sectionSeeAllLink} onClick={showViewMoreModel(section)}>
                  See all
                </button>
              )
            }
          >
            <SectionContentWrapper section={section} courses={courses} showPrice={finalShowPrice} />
          </ThemedSectionComponent>
        );
      })}

      {content?.testimonials && (
        <ShopSectionComponent title={content?.testimonials?.heading} subtitle={content?.testimonials?.subHeading}>
          <StoreTestimonialSlider slides={content?.testimonials?.testimonialList} />
        </ShopSectionComponent>
      )}

      {content?.faqs && (
        <ShopSectionComponent title={content?.faqs?.heading}>
          <StoreFAQComponent faqs={content?.faqs?.faqList || []} />
        </ShopSectionComponent>
      )}

      {currentSection && (
        <ModalComponent
          title={currentSection.heading}
          className={styles.viewMoreModal}
          headerClass={styles.viewMoreModalHeader}
          contentWrapperClass={styles.viewMoreModalContentWrapper}
          contentClass={styles.viewMoreModalContent}
          closeIcon={faChevronLeft}
          onClose={() => setCurrentSection(null)}
          show
        >
          {currentSection?.sectionList?.map((course) => (
            <ProductComponent
              key={`${currentSection.sectionId}-${course.courseId}`}
              product={course}
              showPrice={finalShowPrice}
              responsive
            />
          ))}
        </ModalComponent>
      )}

      <SupportComponent key={category} metaData={content.meta} />
    </ShopContainer>
  );
}

export const getServerSideProps = wrapper.getServerSideProps(() => async ({ params, ...ctx }) => {
  try {
    const { authToken } = getAuthState(ctx);

    const response = await javaApi.get('store-ws/api/v1/store/homepage', {
      params: { menuSlug: params.category },
      headers: { Authorization: `Bearer ${authToken}` },
    });

    if (response?.data?.success) {
      const { content, showPrice } = response.data.data || {};
      const { faqScript } = content.meta;

      delete content.meta.faqScript;

      let faqs = [];

      // mapping faq script to faqs
      try {
        faqs = JSON.parse(faqScript).mainEntity || [];
      } catch (error) {
        faqs = [];
      }
      return {
        props: {
          header: { sticky: false },
          content: {
            ...content,
            meta: {
              ...content.meta,
              faqs,
            },
          },
          showPrice,
          category: params.category,
          pageSeoObject: {
            title:
              content?.meta?.metaTitle || 'Online Smart Course for UPSC/IAS, SSC, Bank, State PSC, UGC NET, Railways',
            keywords: content?.meta?.metaKeyword,
            metaDescription: content?.meta?.menuDescription || null,
          },
        },
      };
    }

    return { notFound: true };
  } catch (error) {
    return { notFound: true };
  }
});

export default ViewAllPage;
